import { useState } from 'react';
import { useSnackbars } from '@customerio/pluma-components/react';
import { useAnalytics } from '@/services/analytics';
import { isEmptyString } from '@/utils/string';
import { useSendEvent } from '../hooks';
import { eventDataAttributeNames, hasAnyEventData, toEventDataPayload } from '../utils/event-data';
import { eventDataError, eventNameError } from '../utils/validation';
import type { EventDataEditorType } from './event-data-editor';
import type { SendEventRecipient } from './recipient-picker';

/**
 * What the modal is composing, and what happens when it is sent.
 *
 * Kept out of the modal so the markup reads as the form it is: the rules
 * about what can be sent, what is recorded, and what is said afterwards are
 * all here.
 *
 * Not the ADR-0001 form pattern, and deliberately so: neither editor is a
 * field `registerPluma` can drive — one is Monaco, the other a grid of rows
 * that are added and removed — and both write the same single value, which a
 * Zod schema would validate twice over. The rules live in `utils/validation`
 * instead, where the Ember originals they match can be read beside them. A
 * form of plain fields should still follow the ADR.
 */

type SendEventFormOptions = {
  workspaceId: number;
  /** Where the modal was opened from. Recorded on every analytics event. */
  source: string;
  /** Whether this send retries one that was already recorded. */
  isResend: boolean;
  /** The viewer may not send at all. */
  isDisabled: boolean;
  initial: { recipient: SendEventRecipient | null; name: string; data: string };
  /** What the workspace calls one person, for the reason a send is blocked. */
  personSingular: string;
  onSent: (() => void) | undefined;
  onClose: () => void;
};

export function useSendEventForm({
  workspaceId,
  source,
  isResend,
  isDisabled,
  initial,
  personSingular,
  onSent,
  onClose,
}: SendEventFormOptions) {
  const analytics = useAnalytics();
  const snackbars = useSnackbars();
  const send = useSendEvent();
  // Keep whatever the caller handed over, even a payload the grid cannot show
  // as rows — a resend's recorded attributes are the point of the resend. The
  // editor opens on JSON for anything that arrived with data, because that is
  // the only editor that can show all of it.
  const opensWithData = hasAnyEventData(initial.data);
  const [recipient, setRecipient] = useState<SendEventRecipient | null>(initial.recipient);
  const [name, setName] = useState(initial.name);
  const [data, setData] = useState(opensWithData ? initial.data : '');
  const [editorType, setEditorType] = useState<EventDataEditorType>(opensWithData ? 'JSON' : 'Simple');
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [dataError, setDataError] = useState<string | undefined>(undefined);

  // How many attributes, not which. A key identifies a person as readily as
  // its value, which is why the grid masks both.
  const attributesOf = (payload: string) => ({ 'Event Attribute Count': eventDataAttributeNames(payload).length });

  const record = {
    Source: source,
    'Is Resend': isResend ? ('Yes' as const) : ('No' as const),
  };

  const trackError = (message: string) => {
    void analytics.track('events', 'errorEncountered', {
      ...record,
      'Error Message': message,
      // The name a send would have carried, so a refusal and a success are
      // counted under the same one.
      'Event Name': name.trim(),
      ...attributesOf(data),
      'Event Attribute Editor Type': editorType,
    });
  };

  /** Why the send is not offered yet, or nothing when it is. */
  const blockedReason = (): string | undefined => {
    if (isDisabled) {
      return 'Contact an Admin to access this feature';
    }
    if (recipient == null) {
      return `Select a ${personSingular}`;
    }
    if (isEmptyString(name)) {
      return 'Enter the event name to send';
    }
    return undefined;
  };

  /**
   * Whether the values are sendable. Only a rejected payload is recorded: a
   * missing event name is a slip anyone corrects in a second, and counting
   * it would bury the payloads the endpoint actually refuses.
   */
  const validate = (): boolean => {
    const nextNameError = eventNameError(name);
    const nextDataError = eventDataError(data);
    setNameError(nextNameError);
    setDataError(nextDataError);

    if (nextDataError != null) {
      trackError(nextDataError);
    }
    return nextNameError == null && nextDataError == null;
  };

  const submit = async () => {
    if (recipient == null || isDisabled || !validate()) {
      return;
    }

    const payload = toEventDataPayload(data);
    // The name the catalogue will carry. Ember sends it as typed, but the
    // modal this replaces trimmed it, and " purchased " is a second event
    // nobody meant to create.
    const sendName = name.trim();

    try {
      await send.mutateAsync({
        environmentId: String(workspaceId),
        cioId: recipient.cioId,
        name: sendName,
        data: payload,
      });
    } catch {
      // Nothing else reports a failed mutation, and the modal stays open so
      // the same payload can be sent again.
      snackbars.create({
        id: 'send-event.error',
        variant: 'critical',
        message: 'Could not send event. Try again.',
      });
      return;
    }

    // No recipient here. A snackbar is plain text, so it cannot be masked the
    // way every other place this address appears is, and the event name is
    // enough to say which send succeeded.
    snackbars.create({ id: 'send-event.success', message: `${sendName} event sent!` });

    void analytics.track('events', 'sendCompleted', {
      ...record,
      'Event Name': sendName,
      ...attributesOf(payload),
      'Event Attribute Editor Type': editorType,
    });

    onClose();
    onSent?.();
  };

  return {
    recipient,
    setRecipient,
    name,
    setName,
    data,
    setData,
    editorType,
    setEditorType,
    nameError,
    checkName: () => setNameError(eventNameError(name)),
    dataError,
    blockedReason: blockedReason(),
    isSending: send.isPending,
    submit,
  };
}
